import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DashboardRow {
  readonly dashboardId: string;
  readonly dashboardCode: string;
  readonly dashboardName: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const WIDGET_TYPES = ['KPI_CARD', 'CHART', 'TABLE', 'GAUGE', 'TREND', 'PIE', 'BAR', 'MAP', 'CALENDAR', 'SCORECARD'];

/**
 * M28 — Dashboard Builder (§16 / §32). Assembles operational dashboards from the
 * ten-widget library (KPI card, chart, table, gauge, trend, pie, bar, map,
 * calendar, scorecard). Lists every dashboard_definition (T372) with its
 * lifecycle status, filterable by status. Supports create dashboard → publish
 * and register a reusable widget in the widget library (T373), each wired to
 * the guarded /v1/lcnc/dashboards write API.
 */
export function M28DashboardBuilderPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [widgetName, setWidgetName] = useState('');
  const [widgetType, setWidgetType] = useState('KPI_CARD');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DashboardRow[] }>(`/v1/lcnc/dashboards${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.dashboardBuilder.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.dashboardBuilder.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.dashboardBuilder.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: DashboardRow }>(`/v1/lcnc/dashboards`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), dashboardName: name.trim() },
        token,
      });
      setNotice(tr('m28.dashboardBuilder.noticeCreated', { code: r.data.dashboardCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.dashboardBuilder.verbBuild'), 'DASHBOARD_BUILD'));
    } finally {
      setBusy(false);
    }
  };

  const registerWidget = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!widgetName.trim()) { setActionError(tr('m28.dashboardBuilder.validationWidgetName')); return; }
    try {
      await apiRequest(`/v1/lcnc/dashboards/widgets`, {
        method: 'POST',
        body: { widgetName: widgetName.trim(), widgetType },
        token,
      });
      setNotice(tr('m28.dashboardBuilder.noticeWidget', { name: widgetName.trim() }));
      setWidgetName('');
    } catch (err) {
      setActionError(failMessage(err, tr('m28.dashboardBuilder.verbWidget'), 'WIDGET_MANAGE'));
    }
  };

  const publish = async (d: DashboardRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/lcnc/dashboards/${d.dashboardId}/publish`, { method: 'POST', body: {}, token });
      setNotice(tr('m28.dashboardBuilder.noticePublished', { code: d.dashboardCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.dashboardBuilder.verbBuild'), 'DASHBOARD_BUILD'));
    }
  };

  return (
    <main aria-label="dashboard-builder">
      <h1>{tr('m28.dashboardBuilder.title')}</h1>

      <section aria-label="create-dashboard">
        <h2>{tr('m28.dashboardBuilder.newDashboardHeading')}</h2>
        <form aria-label="dashboard-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.dashboardBuilder.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.dashboardBuilder.fieldName')}<input aria-label="field-dashboard-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <button type="submit" aria-label="submit-dashboard" disabled={busy}>{busy ? tr('m28.dashboardBuilder.creating') : tr('m28.dashboardBuilder.createDashboard')}</button>
        </form>
        <h2>{tr('m28.dashboardBuilder.registerWidgetHeading')}</h2>
        <form aria-label="widget-form" onSubmit={(e) => void registerWidget(e)}>
          <label>{tr('m28.dashboardBuilder.fieldWidgetName')}<input aria-label="field-widget-name" value={widgetName} onChange={(e) => setWidgetName(e.target.value)} /></label>
          <label>{tr('m28.dashboardBuilder.fieldType')}
            <select aria-label="field-widget-type" value={widgetType} onChange={(e) => setWidgetType(e.target.value)}>
              {WIDGET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-widget">{tr('m28.dashboardBuilder.registerWidget')}</button>
        </form>
        {actionError && <p role="alert" aria-label="dashboard-error">{actionError}</p>}
        {notice && <p aria-label="dashboard-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.dashboardBuilder.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.dashboardBuilder.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.dashboardBuilder.loading')}</p> : (
        <>
          <p aria-label="dashboard-count">{tr('m28.dashboardBuilder.count', { count: rows.length })}</p>
          <table aria-label="dashboard-table">
            <thead><tr><th>{tr('m28.dashboardBuilder.colCode')}</th><th>{tr('m28.dashboardBuilder.colName')}</th><th>{tr('m28.dashboardBuilder.colStatus')}</th><th>{tr('m28.dashboardBuilder.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.dashboardId}>
                  <td>{d.dashboardCode}</td><td>{d.dashboardName}</td><td>{d.status}</td>
                  <td><button type="button" aria-label={`publish-${d.dashboardCode}`} onClick={() => void publish(d)}>{tr('m28.dashboardBuilder.btnPublish')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
